import {
  Button,
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  DropDownMenu,
  DropDownMenuItem,
  Icon,
  IconSize,
  IconVariants,
  Shape,
  Size,
  Text,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { CharacterType, usePersona, useUserSettings } from "@next-platform/client-common-core";
import { Avatar } from "@nextui-org/react";
import React, { MouseEvent, useCallback, useMemo, useState } from "react";

import { Col, LinkWrapper, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl, replaceMessageTokens } from "@/utils";
import { renderCustomMarkdownToHtml } from "@/utils/renderCustomMarkdownToHtml";

import ChatsStats from "../ChatsStats/ChatsStats";
import { ChatCardProps } from "../types";

const BOT_UNAVAILABLE_AVATAR_URL = "/Assets/fallback-avatar.png"; // Fallback avatar URL when character avatar is unavailable

const ChatCard = ({ conversation, dropdownItems, isMultiSelectMode = false, isSelected = false, onToggleSelection }: ChatCardProps) => {
  const { blurNSFW } = useUserSettings();
  const { getUserPersonaById } = usePersona();
  const { isMobile } = useMedia();
  const [isContentOverflowing, setIsContentOverflowing] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const updatedDropdownItems: DropDownMenuItem[] = dropdownItems.map((item) => ({
    ...item,
    classNames: {
      label: "!text-label-md",
    },
  }));

  const {
    last_text_message,
    message_count: interaction_count,
    user_persona_id,
    id,
    label,
    last_message_id,
    character_id,
    character,
  } = conversation || {};

  const message = replaceMessageTokens(last_text_message?.content, {
    characterName: character?.name,
  });

  const html = useMemo(() => renderCustomMarkdownToHtml(message), [message]);
  const interactionCount = interaction_count || 0;

  const userName = getUserPersonaById(user_persona_id)?.name;
  const isEmptyConversation = !last_message_id; // Check if there are no messages in the conversation

  const displayMessage = !last_message_id || Boolean(message);

  const avatarSrc = character?.avatar_url
    ? (getAvatarUrl(character.avatar_url, { preset: AvatarPreset.AVATAR_256X256 }) as string)
    : BOT_UNAVAILABLE_AVATAR_URL;
  const avatartIsBlurred = blurNSFW && character?.avatar_is_nsfw;
  const isGroupConversation = character?.type === CharacterType.META;

  const contentRefCallback = useCallback((element: HTMLParagraphElement | null) => {
    if (element) {
      const isOverflowing = element.scrollHeight > element.clientHeight;
      if (isOverflowing) {
        setIsContentOverflowing(isOverflowing);
      }
    }
  }, []);

  if (!conversation) {
    return null;
  }
  const handleExpandCollapse = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsContentExpanded(!isContentExpanded);
  };

  const handleDeleteSelectionToggle = () => {
    if (isMultiSelectMode && id) {
      onToggleSelection?.(id);
    }
  };

  return (
    <Col
      className={cn(
        "w-full bg-gray-3 border-1 border-solid border-gray-5 dark:border-gray-8 relative rounded-lg",
        !isContentExpanded && "min-h-[140px] max-mob:min-h-[138px]",
        isMultiSelectMode && "cursor-pointer min-h-[120px]",
        isSelected && "!bg-blue-1 ",
      )}
      onClick={handleDeleteSelectionToggle}
    >
      <LinkWrapper to={!isMultiSelectMode ? `/chat/${character_id}/${id}` : undefined}>
        <Row className="gap-lg flex-1 cursor-pointer p-md rounded-lg">
          {isMultiSelectMode && (
            <div className="p-md">
              <Checkbox
                checked={isSelected}
                onChange={handleDeleteSelectionToggle}
                size={Size.MEDIUM}
                onClick={(event: MouseEvent) => event.stopPropagation()}
              />
            </div>
          )}
          <div>
            <Avatar
              src={avatarSrc}
              imgProps={{ loading: "lazy" }}
              className={cn("!w-[72px] sm:!w-[90px] !h-[72px] sm:!h-[90px] rounded-[8px]", avatartIsBlurred ? "blur" : "blur-none")}
            />
          </div>
          <Col className="gap-1 max-mob:gap-0.5">
            <Row className="justify-between items-center">
              <Row className={cn("flex-grow justify-between max-mob:flex-col", !isMultiSelectMode && "pr-8")}>
                <Row className="justify-between">
                  <Row className="items-center flex-wrap gap-[4px]">
                    {isGroupConversation && <Icon icon={IconVariants.USERS} size={IconSize.XSMALL} className="fill-text2 text-text2" />}
                    <Text variant={isMobile ? TextVariants.LABEL_LARGE_BOLD : TextVariants.HEADING_6_MEDIUM} className="sm:font-[500] text-gray-12">
                      {label || character?.name || <Trans i18nKey="chats:page.common.fallbackChatbotLabel" />}
                    </Text>
                    {!!userName && (
                      <Text variant={isMobile ? TextVariants.LABEL_SMALL_BOLD : TextVariants.HEADING_6_MEDIUM} className="text-gray-10">
                        •
                      </Text>
                    )}
                    {!!userName && (
                      <Text
                        variant={isMobile ? TextVariants.LABEL_SMALL_REGULAR : TextVariants.LABEL_MEDIUM_REGULAR}
                        className=" font-[400] text-gray-11"
                      >
                        {userName}
                      </Text>
                    )}
                  </Row>
                </Row>
                <ChatsStats timestamp={conversation.last_text_message?.createdAt} interactionCount={interactionCount} />
              </Row>
            </Row>
            <Row>
              <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10 font-[400] [overflow-wrap:anywhere] line-clamp-1">
                {conversation.character?.title}
              </Text>
            </Row>
            {displayMessage && (
              <Col className="items-start gap-1 max-mob:gap-0.5">
                <Text
                  variant={isMobile ? TextVariants.PARAGRAPH_SMALL_REGULAR : TextVariants.PARAGRAPH_MEDIUM_REGULAR}
                  className="text-gray-11 font-[400] [overflow-wrap:anywhere]"
                  style={{ lineHeight: isMobile ? "20px" : "24px" }}
                >
                  {isEmptyConversation ? (
                    <Trans i18nKey="chats:page.common.displayMessage" />
                  ) : (
                    <p
                      ref={contentRefCallback}
                      className={cn(isMultiSelectMode || !isContentExpanded ? "line-clamp-2" : "line-clamp-[10]")}
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  )}
                </Text>
                {!isMultiSelectMode && isContentOverflowing && (
                  <Button
                    variant={ButtonVariant.TRANSPARENT}
                    iconEnd={isContentExpanded ? IconVariants.CHEVRON_UP : IconVariants.CHEVRON_DOWN}
                    size={ButtonSize.SMALL}
                    className="p-0 size-min text-blue-12"
                    onClick={handleExpandCollapse}
                  >
                    {isContentExpanded ? <Trans i18nKey="chats:page.common.view.less" /> : <Trans i18nKey="chats:page.common.view.more" />}
                  </Button>
                )}
              </Col>
            )}
          </Col>
        </Row>
      </LinkWrapper>
      {!isMultiSelectMode && (
        <div className="absolute top-1.5 max-mob:top-1 right-0.5">
          <DropDownMenu listItems={updatedDropdownItems}>
            <ButtonIcon
              variant={ButtonVariant.TEXT}
              icon={IconVariants.THREE_DOTS}
              size={ButtonSize.MEDIUM}
              shape={Shape.SQUARE}
              className="hover:!bg-opacity-50"
            />
          </DropDownMenu>
        </div>
      )}
    </Col>
  );
};

export default ChatCard;
